import React, { Children, isValidElement } from 'react';
import type { ReactNode } from 'react';
import { cssMap } from '@compiled/react';

import { Box } from '@atlaskit/primitives/compiled/box';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	container: {
		display: 'flex',
		flexDirection: 'column',
		width: '100%',
		borderColor: token('color.border'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		borderRadius: token('radius.large'),
		backgroundColor: token('elevation.surface'),
		boxSizing: 'border-box',
	},
	row: {
		minWidth: '0px',
	},
	firstRow: {
		borderTopLeftRadius: token('radius.large'),
		borderTopRightRadius: token('radius.large'),
	},
	lastRow: {
		borderBottomLeftRadius: token('radius.large'),
		borderBottomRightRadius: token('radius.large'),
	},
	divider: {
		borderTopColor: token('color.border'),
		borderTopStyle: 'solid',
		borderTopWidth: token('border.width'),
	},
});

export type PressableGroupProps = {
	/**
	 * One row per child. A single child renders as a plain rounded box; several share one
	 * card with straight dividers between them and rounding only on the first and last row.
	 *
	 * No `overflow: hidden` is used, so a row's own focus ring is never clipped. A row that
	 * paints its own hover/press background edge-to-edge should set `borderRadius: 'inherit'`
	 * on that background layer so its corners match the wrapper's.
	 */
	children?: ReactNode;
	testId?: string;
};

/**
 * The shared card shape for a group of pressable rows — used under Goals, Projects, and
 * People & Agents in the Teamwork Graph preview panel. Owns only the group's shape (rounding,
 * dividers); each row's content, click behaviour, and hover state stay caller-owned, since
 * what a row looks like differs by section.
 */
export const PressableGroup = ({ children, testId }: PressableGroupProps): React.JSX.Element => {
	const rows = Children.toArray(children);
	const lastIndex = rows.length - 1;

	return (
		<Box xcss={styles.container} testId={testId}>
			{rows.map((row, index) => (
				<div
					// eslint-disable-next-line react/no-array-index-key -- `Children.toArray` keys every element it returns, so element rows reuse that key; index is only the fallback for the strings and numbers it passes through unkeyed.
					key={isValidElement(row) ? (row.key ?? index) : index}
					// The wrapper carries the row's corner radius and divider, so it is what a test
					// asserting the group's shape needs to reach.
					data-testid={testId ? `${testId}--row-${index}` : undefined}
					css={[
						styles.row,
						index === 0 && styles.firstRow,
						index === lastIndex && styles.lastRow,
						index > 0 && styles.divider,
					]}
				>
					{row}
				</div>
			))}
		</Box>
	);
};
